import { Badge } from "@/components/kit";
import { cn } from "@/lib/utils";
import type {
  CustomJobItemDTO,
  CustomJobLineType,
} from "@/modules/custom-jobs/domain/types";
import { formatLak } from "@/shared/lib/currency";
import { CUSTOM_JOB_LINE_TYPE_LABEL, CUSTOM_JOB_LINE_TYPE_BADGE_VARIANT } from "./job-status";

const LINE_TYPE_ROW_CLASS: Record<CustomJobLineType, string> = {
  material: "border-secondary/25 bg-secondary/5",
  labor: "border-primary/20 bg-primary/5",
  other: "border-border/60 bg-muted/30",
};

function groupItemsByType(items: CustomJobItemDTO[]) {
  const groups: Array<{
    lineType: CustomJobLineType;
    items: CustomJobItemDTO[];
  }> = [];
  const order: CustomJobLineType[] = ["material", "labor", "other"];

  for (const lineType of order) {
    const typed = items.filter((item) => item.lineType === lineType);
    if (typed.length > 0) groups.push({ lineType, items: typed });
  }

  return groups;
}

type CustomJobDetailItemsPanelProps = {
  items: CustomJobItemDTO[];
  subtotal: string;
  discountAmount: string;
  total: string;
  cogsTotal?: string | null;
};

export function CustomJobDetailItemsPanel({
  items,
  subtotal,
  discountAmount,
  total,
  cogsTotal,
}: CustomJobDetailItemsPanelProps) {
  const groups = groupItemsByType(items);

  return (
    <div className="flex flex-col gap-4">
      {groups.map((group) => {
        const rowClassName = LINE_TYPE_ROW_CLASS[group.lineType];
        return (
          <div key={group.lineType} className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Badge
                variant={CUSTOM_JOB_LINE_TYPE_BADGE_VARIANT[group.lineType]}
                className="rounded-full text-[10px]"
              >
                {CUSTOM_JOB_LINE_TYPE_LABEL[group.lineType]}
              </Badge>
              <span className="text-muted-foreground text-xs">
                {group.items.length} ລາຍການ
              </span>
            </div>
            <ul className="flex flex-col gap-2">
              {group.items.map((item) => (
                <li
                  key={item.id}
                  className={cn(
                    "rounded-xl border px-3 py-3 sm:px-4",
                    rowClassName,
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      {item.lineType === "material" && item.productName ? (
                        <>
                          <p className="font-medium text-sm leading-snug">
                            {item.productName}
                          </p>
                          {item.variantName ? (
                            <p className="mt-0.5 text-muted-foreground text-xs">
                              {item.variantName}
                            </p>
                          ) : null}
                        </>
                      ) : (
                        <p className="font-medium text-sm leading-snug">
                          {item.description}
                        </p>
                      )}
                      <p className="mt-1 text-[11px] text-muted-foreground tabular-nums">
                        {formatLak(item.unitPrice)} × {item.quantity}
                      </p>
                      {item.cogsTotal ? (
                        <p className="mt-0.5 text-[11px] text-muted-foreground tabular-nums">
                          COGS {formatLak(item.cogsTotal)}
                        </p>
                      ) : null}
                    </div>
                    <span className="shrink-0 font-display text-secondary tabular-nums">
                      {formatLak(item.lineTotal)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );
      })}

      <div className="flex flex-col gap-1 border-secondary/20 border-t pt-4 text-sm">
        <div className="flex justify-between text-muted-foreground">
          <span>ຍອດລວມລາຍການ</span>
          <span className="tabular-nums">{formatLak(subtotal)}</span>
        </div>
        {Number(discountAmount) > 0 ? (
          <div className="flex justify-between text-muted-foreground">
            <span>ສ່ວນລົດ</span>
            <span className="text-destructive tabular-nums">
              −{formatLak(discountAmount)}
            </span>
          </div>
        ) : null}
        <div className="flex justify-between font-display text-foreground text-lg">
          <span>ລວມ</span>
          <span className="text-secondary tabular-nums">{formatLak(total)}</span>
        </div>
        {cogsTotal ? (
          <div className="flex justify-between text-muted-foreground text-xs">
            <span>COGS ວັດຖຸດິບ</span>
            <span className="tabular-nums">{formatLak(cogsTotal)}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
